/**
 * VH Staff Accept Invitation Page
 *
 * Validates invitation code and allows staff to accept.
 * Staff sets their password during acceptance and is logged in automatically.
 */
import { useEffect, useState, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  AlertCircle,
  Loader2,
  Mail,
  Building2,
  Clock,
  Lock,
  Eye,
  EyeOff
} from 'lucide-react';
import { apiClient } from '@/services/api/apiClient';

interface PasswordRequirement {
  label: string;
  met: boolean;
}

interface InvitationDetails {
  valid: boolean;
  restaurant_name: string;
  inviter_name: string | null;
  expires_at: string;
  restaurant_logo_url: string | null;
}

const VHStaffAcceptInvitation = () => {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');

  const [status, setStatus] = useState<'validating' | 'valid' | 'accepting' | 'success' | 'error'>('validating');
  const [error, setError] = useState('');
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Password requirements (must match VHStaffResetPassword.tsx)
  const passwordRequirements: PasswordRequirement[] = useMemo(() => [
    { label: 'At least 12 characters', met: password.length >= 12 },
    { label: 'One uppercase letter', met: /[A-Z]/.test(password) },
    { label: 'One lowercase letter', met: /[a-z]/.test(password) },
    { label: 'One number', met: /[0-9]/.test(password) },
    { label: 'One special character', met: /[!@#$%^&*(),.?":{}|<>\-_=+\[\]\\;'`~]/.test(password) },
  ], [password]);

  const allRequirementsMet = passwordRequirements.every(req => req.met);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
  const [restaurantName, setRestaurantName] = useState('');

  useEffect(() => {
    if (code) {
      validateInvitation();
    } else {
      setStatus('error');
      setError('No invitation code provided');
    }
  }, [code]);

  const validateInvitation = async () => {
    try {
      const response = await apiClient.get(`/vh-staff/validate-invitation?code=${code}`);
      setInvitation(response.data);
      setStatus('valid');
    } catch (err: any) {
      setStatus('error');
      setError(err.response?.data?.detail || 'Invalid or expired invitation');
    }
  };

  const handleAccept = async () => {
    // Validate form
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    if (!password) {
      setError('Please create a password');
      return;
    }
    if (!allRequirementsMet) {
      setError('Please meet all password requirements');
      return;
    }
    if (!passwordsMatch) {
      setError('Passwords do not match');
      return;
    }

    setError('');
    setStatus('accepting');

    try {
      const response = await apiClient.post('/vh-staff/accept-invitation', {
        invitation_code: code,
        email,
        name: name || null,
        password
      });

      if (response.data.success) {
        const activeRestaurant = response.data.active_restaurant;
        setRestaurantName(activeRestaurant?.name || 'Restaurant');
        setStatus('success');

        // Clear any demo flags to prevent auth conflicts
        // VH Staff and Demo use separate auth systems
        localStorage.removeItem('isDemo');
        localStorage.removeItem('demoPlan');

        // Set VH Staff localStorage flags BEFORE redirect
        // This is critical for VHStaffProtectedRoute to recognize vh_staff user
        localStorage.setItem('user_type', 'vh_staff');
        localStorage.setItem('staff_name', response.data.name || '');
        localStorage.setItem('restaurant_name', activeRestaurant?.name || '');
        localStorage.setItem('restaurant_slug', activeRestaurant?.slug || '');

        // Store full staff data for later use
        localStorage.setItem('vh_staff_data', JSON.stringify({
          staff_id: response.data.staff_id,
          email: response.data.email,
          name: response.data.name,
          restaurants: response.data.restaurants,
          active_restaurant: activeRestaurant
        }));

        // Redirect to VH Staff dashboard after brief success message
        setTimeout(() => {
          window.location.href = response.data.redirect_url || '/vh-staff-dashboard';
        }, 2000);
      }
    } catch (err: any) {
      setStatus('valid'); // Go back to form
      setError(err.response?.data?.detail || 'Failed to accept invitation');
    }
  };

  const formatExpiryDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });
  };

  if (status === 'validating') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <Loader2 className="h-12 w-12 text-[#FDB913] animate-spin mx-auto mb-4" />
          <h1 className="text-xl font-medium text-gray-900">Validating invitation...</h1>
        </motion.div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center"
        >
          <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Account Created!</h1>
          <p className="text-gray-500 mb-4">
            Redirecting you to the {restaurantName} dashboard...
          </p>
          <div className="h-1 w-32 bg-gray-200 rounded-full mx-auto overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: '100%' }}
              transition={{ duration: 2 }}
              className="h-full bg-[#FDB913]"
            />
          </div>
        </motion.div>
      </div>
    );
  }

  if (status === 'error' && !invitation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center"
        >
          <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Invalid Invitation</h1>
          <p className="text-gray-500 mb-6">{error}</p>
          <Link
            to="/"
            className="inline-block px-6 py-3 bg-[#FDB913] text-gray-900 rounded-lg font-semibold hover:bg-[#e5a811] transition-colors"
          >
            Back to Home
          </Link>
        </motion.div>
      </div>
    );
  }

  // Valid invitation - show accept form with spacious horizontal layout
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-6 md:p-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-5xl overflow-hidden"
      >
          <div className="grid md:grid-cols-5 min-h-[550px]">
            {/* Left Column - Invitation Info (2/5 width) */}
            <div className="md:col-span-2 bg-gradient-to-br from-gray-900 to-gray-800 p-8 md:p-10 flex flex-col">
              {/* Invitation Header */}
              <div className="mb-8">
              <h1 className="text-3xl font-bold text-white mb-3">You're Invited!</h1>
              <p className="text-gray-300 text-lg">
                Join the team at{' '}
                <span className="text-white font-semibold">{invitation?.restaurant_name}</span>
              </p>
            </div>

            {/* Invitation Details */}
            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-4 text-gray-300">
                <div className="h-10 w-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Building2 className="h-5 w-5 text-[#FDB913]" />
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide">Restaurant</p>
                  <p className="text-white font-medium">{invitation?.restaurant_name}</p>
                </div>
              </div>

              {invitation?.inviter_name && (
                <div className="flex items-center gap-4 text-gray-300">
                  <div className="h-10 w-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="h-5 w-5 text-[#FDB913]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide">Invited by</p>
                    <p className="text-white font-medium">{invitation.inviter_name}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-4 text-gray-300">
                <div className="h-10 w-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Clock className="h-5 w-5 text-[#FDB913]" />
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide">Expires</p>
                  <p className="text-white font-medium">
                    {invitation?.expires_at ? formatExpiryDate(invitation.expires_at) : 'Soon'}
                  </p>
                </div>
              </div>
            </div>

            {/* What You'll Be Able To Do */}
            <div className="mt-auto pt-6 border-t border-white/10">
              <p className="text-sm font-medium text-gray-300 mb-4">
                As a staff member, you'll be able to:
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-gray-300 text-sm">
                  <CheckCircle className="h-4 w-4 text-[#FDB913] flex-shrink-0" />
                  View and manage incoming orders
                </li>
                <li className="flex items-center gap-3 text-gray-300 text-sm">
                  <CheckCircle className="h-4 w-4 text-[#FDB913] flex-shrink-0" />
                  Update wait times and order status
                </li>
                <li className="flex items-center gap-3 text-gray-300 text-sm">
                  <CheckCircle className="h-4 w-4 text-[#FDB913] flex-shrink-0" />
                  Monitor AI usage and call minutes
                </li>
                <li className="flex items-center gap-3 text-gray-300 text-sm">
                  <CheckCircle className="h-4 w-4 text-[#FDB913] flex-shrink-0" />
                  View payment status in Stripe
                </li>
              </ul>
            </div>
          </div>

          {/* Right Column - Form (3/5 width) */}
          <div className="md:col-span-3 p-8 md:p-10 lg:p-12 flex flex-col justify-center">
            <div className="max-w-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Create Your Account</h2>
              <p className="text-gray-500 mb-8">Fill in your details to get started</p>

              {/* Error Message */}
              {error && (
                <div className="p-4 bg-red-50 text-red-700 rounded-xl flex items-center gap-3 text-sm mb-6 border border-red-100">
                  <AlertCircle className="h-5 w-5 flex-shrink-0" />
                  {error}
                </div>
              )}

              {/* Accept Form */}
              <div className="space-y-6">
                {/* Email and Name in a row on larger screens */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FDB913] focus:border-transparent bg-gray-50 hover:bg-white transition-colors"
                      autoComplete="email"
                    />
                    <p className="text-xs text-gray-400 mt-2">
                      Must match your invitation email
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Your Name <span className="text-gray-400">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="John Smith"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FDB913] focus:border-transparent bg-gray-50 hover:bg-white transition-colors"
                    />
                    <p className="text-xs text-gray-400 mt-2">
                      How you'll appear in the system
                    </p>
                  </div>
                </div>

                {/* Password fields in a row */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Create Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Min. 12 characters"
                        className="w-full pl-12 pr-12 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FDB913] focus:border-transparent bg-gray-50 hover:bg-white transition-colors"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Confirm Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm password"
                        className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FDB913] focus:border-transparent bg-gray-50 hover:bg-white transition-colors"
                        autoComplete="new-password"
                      />
                    </div>
                  </div>
                </div>

                {/* Password requirements checklist */}
                {password && (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                      {passwordRequirements.map((req) => (
                        <div key={req.label} className="flex items-center gap-2 text-sm">
                          <span className={req.met ? 'text-green-500' : 'text-gray-300'}>
                            {req.met ? '✓' : '○'}
                          </span>
                          <span className={req.met ? 'text-green-700' : 'text-gray-500'}>
                            {req.label}
                          </span>
                        </div>
                      ))}
                    </div>
                    {confirmPassword && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className={passwordsMatch ? 'text-green-500' : 'text-red-500'}>
                          {passwordsMatch ? '✓' : '✗'}
                        </span>
                        <span className={passwordsMatch ? 'text-green-700' : 'text-red-600'}>
                          {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                <button
                  onClick={handleAccept}
                  disabled={status === 'accepting' || !email || !allRequirementsMet || !passwordsMatch}
                  className="w-full py-4 bg-[#FDB913] text-gray-900 rounded-xl font-semibold hover:bg-[#e5a811] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 transition-all hover:shadow-lg hover:shadow-[#FDB913]/20 mt-4"
                >
                  {status === 'accepting' ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <CheckCircle className="h-5 w-5" />
                  )}
                  Create Account & Sign In
                </button>
              </div>

              {/* Powered by Dine AI */}
              <div className="mt-8 pt-6 border-t border-gray-100 text-center">
                <p className="text-xs text-gray-400">
                  Powered by{' '}
                  <span className="font-medium text-gray-600">
                    Dine <span className="text-[#FDB913]">AI</span>
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default VHStaffAcceptInvitation;
